import type { Preferences } from 'api/types/preferences.types';
import cx from 'classnames';
import { StyledFormCheck } from 'components/molecules/StyledFormCheck';
import ToggleSwitch from 'components/molecules/ToggleSwitch';
import type { CSSProperties } from 'react';
import React, { useCallback } from 'react';
import { Col, ListGroup, Row } from 'react-bootstrap';
import styled from 'styled-components';

const StyledHeading = styled.h3`
  font-weight: 500;
  color: ${({ theme }) => theme.components.orderInstructions.heading.color} !important;
`;

const StyledSwitchContainer = styled.div`
  margin-left: 20px;
`;

const StyledLabel = styled.label`
  display: 'block';
  margin: 0;
  padding: 0;
`;

const StyledBadgeCheckbox = styled(ListGroup.Item)`
  margin: 4px;
  background-color: ${({ theme }) => theme.components.orderInstructions.badge.backgroundColor.inactive};
  border-radius: 50px;
  padding: 0px;
  color: ${({ theme }) => theme.components.orderInstructions.badge.textColor.inactive};

  & label {
    background: ${({ theme }) => theme.components.orderInstructions.badge.backgroundColor.inactive};
    margin: 0px;

    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }

  span {
    padding: 5px 15px;
    border-radius: 100px;
    border: ${({ theme }) => theme.components.orderInstructions.badge.borderWidth}px solid ${({ theme }) => theme.components.orderInstructions.badge.borderColor.inactive}; 
    color: ${({ theme }) => theme.components.orderInstructions.badge.textColor.inactive};
  }

  input:checked + span  {
    background: ${({ theme }) => theme.components.orderInstructions.badge.backgroundColor.active};
    border: ${({ theme }) => theme.components.orderInstructions.badge.borderWidth}px solid ${({ theme }) => theme.components.orderInstructions.badge.borderColor.active}; 
    color: ${({ theme }) => theme.components.orderInstructions.badge.textColor.active}; !important;
  }

  & input {
    position: absolute;
    display: none;
    color: ${({ theme }) => theme.components.orderInstructions.badge.textColor.inactive}; !important;
  }
`;

const StyledListGroup = styled(ListGroup)<{ checkboxType?: 'checkbox' | 'badge' }>`
  flex-direction: ${({ checkboxType }) => (checkboxType === 'checkbox' ? 'column' : 'row')};
  flex-wrap: wrap;

  ${({ checkboxType }) =>
    checkboxType === 'badge' &&
    `
    .list-group-item {
      border: unset !important;
    }
  `}
`;

const StyledOptionTextsRow = styled.div`
  flex: 1;
`;

const OptionTitle = styled.h4<{ isbold?: string }>`
  font-weight: ${({ isbold }) => (isbold === 'true' ? 500 : 300)};
  word-wrap: break-word;
  color: ${({ theme, isbold }) =>
    isbold === 'true' ? theme.components.orderInstructions.optionTitle.selected : theme.components.orderInstructions.optionTitle.unselected};
`;

const StyledDescription = styled.p`
  color: ${({ theme }) => theme.colors.steelBlue};
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 300;
  line-height: 18px;
  letter-spacing: 0.1px;
`;

interface PreferenceItemsProps {
  category: Preferences.Category;
  preference: Preferences.Preference;
  hideCategoryName?: boolean;
  index: number;
  optionType?: string;
  selectedOptions: string[];
  onChangeValue: (optionType: string) => void;
  preferenceDescription?: string;
  checkboxType?: 'checkbox' | 'badge';
  extraKey?: string;
  categoryNameStyle?: CSSProperties;
  alwaysHighlightOptionTitle?: boolean;
}

const PreferenceItems: React.FC<PreferenceItemsProps> = ({
  category,
  preference,
  hideCategoryName = false,
  index,
  optionType,
  selectedOptions,
  onChangeValue,
  preferenceDescription,
  checkboxType = 'checkbox',
  extraKey = '',
  alwaysHighlightOptionTitle,
  categoryNameStyle,
}) => {
  const handleOptionClick = useCallback(() => {
    onChangeValue(optionType || '');
  }, [onChangeValue, optionType]);

  return (
    <Row className="pt-4">
      {category.text && !hideCategoryName && (
        <Col className={cx('px-2')} xs={12}>
          <StyledHeading style={categoryNameStyle} className="mb-2 p-0">
            {category.text}
          </StyledHeading>
        </Col>
      )}
      <Col xs={12} className="p-0">
        <StyledListGroup checkboxType={checkboxType} variant="flush">
          {category.options.map((item, iGrp) =>
            checkboxType === 'checkbox' ? (
              <StyledLabel htmlFor={`${extraKey}grp-${iGrp}-${index}`}>
                <ListGroup.Item className={cx('bg-white p-0 pl-2 pb-3 border-0')} key={`item-${item.key}`}>
                  <div className="d-flex justify-content-between align-items-center">
                    <StyledOptionTextsRow className="justify-content-center">
                      <OptionTitle isbold={`${selectedOptions.includes(item.key) || alwaysHighlightOptionTitle}`} className="mb-0">
                        {item.text}
                      </OptionTitle>
                      {preferenceDescription && <StyledDescription className="mb-0">{preferenceDescription}</StyledDescription>}
                    </StyledOptionTextsRow>
                    {preference.categories.length === 1 && category.options.length === 1 ? (
                      <StyledSwitchContainer>
                        <ToggleSwitch
                          name={item.key}
                          defaultChecked={item.value}
                          title={item.text}
                          id={`${extraKey}grp-${iGrp}-${index}`}
                          value={item.key}
                          onChange={handleOptionClick}
                        />
                      </StyledSwitchContainer>
                    ) : (
                      <StyledFormCheck
                        name={category.allowMultiSelect ? item.key : category.key}
                        defaultChecked={item.value}
                        title={item.text}
                        custom
                        type={category.allowMultiSelect ? 'checkbox' : 'radio'}
                        id={`${extraKey}grp-${iGrp}-${index}`}
                        label=""
                        value={item.key}
                        onChange={handleOptionClick}
                      />
                    )}
                  </div>
                </ListGroup.Item>
              </StyledLabel>
            ) : (
              <StyledBadgeCheckbox>
                <StyledLabel>
                  <input
                    name={category.allowMultiSelect ? item.key : category.key}
                    defaultChecked={item.value}
                    title={item.text}
                    type={category.allowMultiSelect ? 'checkbox' : 'radio'}
                    id={`${extraKey}grp-${iGrp}-${index}`}
                    value={item.key}
                    onChange={handleOptionClick}
                  />
                  <span>{item.text}</span>
                </StyledLabel>
              </StyledBadgeCheckbox>
            )
          )}
        </StyledListGroup>
      </Col>
    </Row>
  );
};

export default PreferenceItems;
